import React, { useState, useRef, useEffect } from "react";
import "./VideoComments.css";
import Header from "../../Components/Header/Header";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import dayjs from "dayjs";

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const VideoComments = ({ videoId = 1, onBack }) => {
  const [videoDetails, setVideoDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef(null);
  const controlsTimerRef = useRef(null);

  const fetchVideoComments = async () => {
    try {
      setLoading(true);

      const response = await callApi(
        `${videoCreatorEndPoint}video/comment/${videoId}/list?includeVideoDetails=true`,
        "GET",
      );

      if (response?.status) {
        setVideoDetails(response?.data?.videoDetails || null);
        setComments(response?.data?.comments || []);
      }
    } catch (error) {
      console.error("Video Comments API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (videoId) {
      fetchVideoComments();
    }
  }, [videoId]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } else {
      // No real video src — toggle play state on thumbnail
      setIsPlaying((prev) => !prev);
    }
    showControlsBriefly();
  };

  const showControlsBriefly = () => {
    setShowControls(true);
    clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 2500);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = ratio * (duration || 100);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  useEffect(() => {
    return () => clearTimeout(controlsTimerRef.current);
  }, []);

  if (loading) {
    return (
      <div className="vc-page">
        <Header />
        <p>Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="vc-page">
      <Header />

      <div className="vc-breadcrumb">
        <span className="vc-breadcrumb-link" onClick={onBack}>
          All Comments List
        </span>
        <span className="vc-breadcrumb-sep"> / </span>
        <span className="vc-breadcrumb-current">Comments</span>
      </div>

      <div className="vc-content">
        {/* Video Player */}
        <div
          className="vc-video-wrapper"
          onMouseMove={showControlsBriefly}
          onClick={handlePlayPause}
        >
          {videoDetails?.videoUrl ? (
            <video
              ref={videoRef}
              className="vc-video-el"
              src={videoDetails?.videoUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            />
          ) : (
            <div
              className="vc-thumbnail"
              style={{
                backgroundImage: `url(${
                  videoDetails?.thumbnailImage ||
                  videoDetails?.generatedThumbnail
                })`,
              }}
            />
          )}

          {/* Overlay */}
          <div
            className={`vc-overlay ${showControls || !isPlaying ? "visible" : ""}`}
          >
            <button
              className="vc-play-btn"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause();
              }}
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Bottom controls bar */}
            <div
              className="vc-controls-bar"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="vc-progress-track" onClick={handleSeek}>
                <div
                  className="vc-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
                <div
                  className="vc-progress-thumb"
                  style={{ left: `${progressPercent}%` }}
                />
              </div>
              <div className="vc-time-display">
                <span>{formatDuration(currentTime)}</span>
                <span className="vc-time-sep">/</span>
                <span>{duration ? formatDuration(duration) : "--:--"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Video Meta */}
        <h1 className="vc-title">{videoDetails?.title}</h1>
        <div className="vc-meta">
          <span>
            Published on{" "}
            {dayjs(videoDetails?.publishedAt).format("MMM DD, YYYY")}
          </span>
          <p className="vc-stats">
            <span className="vc-dot">•</span>
            <span>{videoDetails?.viewsCount || 0} Views</span>
            <span className="vc-dot">•</span>
            <span>{videoDetails?.likesCount || 0} Likes</span>
          </p>
        </div>

        {/* All Comments Tab */}
        <div className="vc-tab-bar">
          <button className="vc-tab active">All Comments</button>
        </div>

        {/* Comments Section */}
        <div className="vc-comments-section">
          <div className="vc-comments-header">
            <h2 className="vc-comments-title">Comments ({comments.length})</h2>
            <p className="vc-comments-subtitle">
              Latest comments will be displayed at the top of the list.
            </p>
          </div>

          <div className="vc-comments-list">
            {comments.map((comment) => (
              <div key={comment.id} className="vc-comment-item">
                <div
                  className="vc-avatar"
                  style={{ backgroundColor: comment.avatarColor }}
                >
                  {comment.profileUrl ? (
                    <img
                      src={comment.profileUrl}
                      alt={comment.userName}
                      className="profile-avatar"
                    />
                  ) : (
                    <div className="dash-profile-initials">
                      {comment?.userName
                        .split(" ")
                        .map((n) => n[0])
                        .slice(0, 1)
                        .join("")}
                    </div>
                  )}
                </div>
                <div className="vc-comment-body">
                  <div className="vc-comment-top">
                    <span className="vc-comment-author">
                      {comment.fullName || comment.userName}
                    </span>
                    <span className="vc-comment-time">
                      {dayjs(comment.createdAt).format("DD MMM, YYYY hh:mm A")}
                    </span>
                  </div>
                  <p className="vc-comment-text">{comment.comment}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoComments;
